<template>
  <b-modal
    v-model="showModal"
    id="updateRoomRateModal"
    title="Update Room Rate"
    centered
    hide-footer
    hide-header-close
    size="lg"
  >
    <b-row>
      <b-col class="entry-info">
        <b-form-select
          v-model="rateType"
          class="form-control"
          title="Payment Method"
          required
          :options="availableOptions"
        />
      </b-col>

      <b-col class="input-group">
        <currency-input
          v-if="rateType === 2"
          name="Rate"
          :amount.sync="rate"
          :size="10"
          :fieldState="fieldState.rate"
        />
      </b-col>
    </b-row>

    <div v-if="reservation && rateType == 1" class="mt-4 room-rates-table-container">
      <b-table
        class="room-rates-table"
        :fields="fields"
        :items="roomRates"
      >
        <template
          v-for="(field, index) in fields"
          :slot="`head(${ field.key })`"
          slot-scope="data"
        >
          <span :key="index" v-html="data.label" />
        </template>

        <template
          v-for="(field, index) in fields"
          :slot="`cell(${ field.key })`"
          slot-scope="data"
        >
          <b-form-input
            :value="data.item[field.key]"
            :key="index"
            :state="fieldState.page_rate[field.page_id]"
            class="form-control input-page-rate"
            type="number"
            @input="(e) => onUpdatePageRate(field.page_id, e)"
          />
        </template>
      </b-table>
    </div>

    <b-button
      class="mt-3 mr-4"
      variant="outline-danger"
      :disabled="loading"
      @click="hide"
    >
      Close
    </b-button>

    <b-button
      class="mt-3"
      variant="success"
      :disabled="!isValidRates() || loading"
      @click="updateRate"
    >
      Update
    </b-button>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { formatDate } from '@/utils/date-helper';
import { RESERVATION_RATE_TYPES } from '@/config/constants';
import apiBase from '@/utils/api-base';
import cloneDeep from "lodash.clonedeep";

export default {
  name: 'RoomRateModal',

  components: {
    CurrencyInput: () => import('@/components/widgets/CurrencyInput'),
  },

  data() {
    return {
      rateType: 1,
      reservation: null,
      rate: 0,
      availableOptions: [ // todo: Should be a way to drag this in from constants.
        { value: 1, text: 'Nightly' },
        { value: 2, text: 'Fixed' }
        // { value: 6, text: 'Single Nightly Price' }
      ],
      fieldState: {
        rate: null,
        page_rate: []
      },
      loading: false,
      showModal: false,
    }
  },

  computed: {
    fields() {
      if (!this.reservation) {
        return [];
      }

      return this.reservation.pages
        .map((page, index) => ({
          label: `${ formatDate(page.date, 'MMM DD') }<br>${ formatDate(page.date, 'ddd')}`,
          key: formatDate(page.date),
          sortable: false,
          page_id: index,
        }))
    },

    roomRates() {
      if (!this.reservation) {
        return [];
      }

      let rates = {};

      this.reservation.pages
        .sort((a, b) => {
          return b.date > a.date
            ? -1
            : 1;
        })
        .map((page, index) => {
          this.fieldState.page_rate[index] = parseFloat(page.rate) >= 0
            ? null
            : false;

          rates[formatDate(page.date)] = page.rate
        });

      return [ rates ];
    }
  },

  methods: {
    ...mapActions([
      'updateReservation'
    ]),

    show() {
      this.showModal = true;
    },

    hide() {
      this.showModal = false;
      // this.$emit('updated', this.reservation);
    },

    isValidRates() {
      return this.fieldState.page_rate.indexOf(false) === -1;
    },

    onUpdatePageRate(page_index, rate) {
      rate = !isNaN(parseFloat(rate))
        ? parseFloat(rate)
        : null;

      if (rate === null) {
        this.fieldState.page_rate[page_index] = false;
      }

      this.fieldState.page_rate[page_index] = null;
      this.reservation.pages[page_index].rate = rate;
    },

    updateRate() {
      this.reservation.rate_type_id = this.rateType;

      if (this.rateType === RESERVATION_RATE_TYPES.FIXED) {
        this.reservation.rate = this.rate;
        this.loading = true;

        this.updateReservation(this.reservation)
          .then(() => {
            this.hide();
            this.$emit('updated', this.reservation);
          })
          .catch(() => {
            this.errorNotification('Failed to update rate of reservation');
          })
          .finally(() => {
            this.loading = false;
          })

      } else {
        let params = this.reservation.pages
          .map(page => ({
            id: page.id,
            rate: page.rate
          }));

        this.loading = true;

        apiBase.post('/pages/rates', { pages: params })
          .then(res => {
            this.reservation.rate = null;
            return this.updateReservation(this.reservation);
          })
          .then(() => {
            this.hide();
            this.$emit('updated', this.reservation);
          })
          .catch(() => {
            this.errorNotification('Failed to update page rate');
          })
          .finally(() => {
            this.loading = false;
          })
      }
    },

    setReservation(reservation, rate = 0) {
      this.reservation = cloneDeep(reservation);
      this.rateType = reservation.rate_type_id || RESERVATION_RATE_TYPES.NIGHTLY;

      this.rate = rate || 0;
    }
  },
};
</script>

<style lang="scss">
.room-rates-table-container {
  display: inline-block;
  max-width: 100%;
  min-height: 120px;
  overflow: auto !important;
  width: 100%;

  .room-rates-table {
    border-collapse: collapse;
    font-size: 14px;
    margin: 0;

    thead th {
      background-color: #dee2e6;
      padding: 5px 15px;

      &:not(:last-child) {
        border-right: 1px solid #fff;
      }
    }

    tbody td {
      border: 1px solid #dee2e6;
      padding: 5px 8px;
    }

    input {
      border: none;
      font-size: 14px;
      min-width: 80px;
      padding: 5px;
      text-align: center;
    }
  }
}
</style>
